import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Users, Calendar, Settings, Plus, Clock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface Course {
  id: string;
  title: string;
  description: string;
  status: string;
  max_participants: number;
  duration_hours: number;
  registration_start_date: string | null;
  registration_end_date: string | null;
  registration_instructions: string | null;
  auto_approve_registration: boolean;
  created_at: string;
  enrollments?: any[];
}

export default function MyCourses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [registrationSettings, setRegistrationSettings] = useState({
    registration_start_date: '',
    registration_end_date: '',
    registration_instructions: '',
    auto_approve_registration: true
  });

  useEffect(() => {
    fetchMyCourses();
  }, [user]);

  const fetchMyCourses = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('courses')
        .select(`
          *,
          enrollments:enrollments(count)
        `)
        .or(`instructor_id.eq.${user.id},created_by.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch your courses",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateRegistrationSettings = async () => {
    if (!selectedCourse) return;

    try {
      const { error } = await supabase
        .from('courses')
        .update({
          registration_start_date: registrationSettings.registration_start_date || null,
          registration_end_date: registrationSettings.registration_end_date || null,
          registration_instructions: registrationSettings.registration_instructions || null,
          auto_approve_registration: registrationSettings.auto_approve_registration
        })
        .eq('id', selectedCourse.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Registration settings updated successfully"
      });

      await fetchMyCourses();
      setSelectedCourse(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update registration settings",
        variant: "destructive"
      });
    }
  };

  const openRegistrationSettings = (course: Course) => {
    setSelectedCourse(course);
    setRegistrationSettings({
      registration_start_date: course.registration_start_date?.split('T')[0] || '',
      registration_end_date: course.registration_end_date?.split('T')[0] || '',
      registration_instructions: course.registration_instructions || '',
      auto_approve_registration: course.auto_approve_registration
    });
  };

  if (loading) {
    return <div className="p-6">Loading your courses...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">My Courses</h1>
        <Button onClick={() => navigate('/course-proposals')}>
          <Plus className="h-4 w-4 mr-2" />
          Propose New Course
        </Button>
      </div>

      {courses.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">You haven't created any courses yet.</p>
            <Button className="mt-4" onClick={() => navigate('/course-proposals')}>
              Create Your First Course
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {courses.map((course) => {
            const enrollmentCount = course.enrollments?.[0]?.count || 0;
            const registrationOpen = course.registration_start_date && course.registration_end_date
              ? new Date() >= new Date(course.registration_start_date) && new Date() <= new Date(course.registration_end_date)
              : true;

            return (
              <Card key={course.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-lg">{course.title}</CardTitle>
                        <Badge variant={course.status === 'published' ? 'default' : 'secondary'}>
                          {course.status}
                        </Badge>
                        {!registrationOpen && (
                          <Badge variant="destructive">Registration Closed</Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground line-clamp-2 mb-3">
                        {course.description}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {enrollmentCount} enrolled
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {course.duration_hours}h
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Created {new Date(course.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      onClick={() => navigate(`/course-management/${course.id}`)}
                      className="flex-1 sm:flex-none"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Manage Course
                    </Button>
                    
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          variant="outline"
                          onClick={() => openRegistrationSettings(course)}
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Registration Settings
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Registration Settings - {course.title}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label>Registration Start Date</Label>
                            <Input
                              type="date"
                              value={registrationSettings.registration_start_date}
                              onChange={(e) => setRegistrationSettings({
                                ...registrationSettings,
                                registration_start_date: e.target.value
                              })}
                            />
                          </div>
                          <div>
                            <Label>Registration End Date</Label>
                            <Input
                              type="date"
                              value={registrationSettings.registration_end_date}
                              onChange={(e) => setRegistrationSettings({
                                ...registrationSettings,
                                registration_end_date: e.target.value
                              })}
                            />
                          </div>
                          <div>
                            <Label>Registration Instructions</Label>
                            <Input
                              value={registrationSettings.registration_instructions}
                              onChange={(e) => setRegistrationSettings({
                                ...registrationSettings,
                                registration_instructions: e.target.value
                              })}
                              placeholder="Special instructions for enrollment..."
                            />
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="auto_approve"
                              checked={registrationSettings.auto_approve_registration}
                              onChange={(e) => setRegistrationSettings({
                                ...registrationSettings,
                                auto_approve_registration: e.target.checked
                              })}
                            />
                            <Label htmlFor="auto_approve">Auto-approve registrations</Label>
                          </div>
                          <Button onClick={updateRegistrationSettings} className="w-full">
                            Update Settings
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Registration Settings Dialog */}
      {selectedCourse && (
        <Dialog open={!!selectedCourse} onOpenChange={() => setSelectedCourse(null)}>
          {/* Dialog content is handled above */}
        </Dialog>
      )}
    </div>
  );
}